import { useQuery } from '@apollo/client';

import {
  ProviderPlanId,
  TeamMemberRole,
  TeamMemberStatus,
} from '__generated__/GQL';
import useCompanyContext from 'common/hooks/useCompanyContext';
import { useCurrentPricingPlan } from 'common/hooks/useCurrentPricingPlan';
import { isFreeRole } from 'features/Team/utils';

import { UpgradeSummaryPageDocument } from './graphql/upgradeSummaryPage.gql';

const shouldConsiderRole = (role: TeamMemberRole) => !isFreeRole(role);

export const useUpgradePlanInfo = (providerPlanId: ProviderPlanId) => {
  const { companyProfileId = '' } = useCompanyContext();
  const { plan: currentPlan } = useCurrentPricingPlan();

  const { data, ...hook } = useQuery(UpgradeSummaryPageDocument, {
    variables: {
      companyProfileId,
      providerPlanId,
    },
  });

  if (hook.loading || !data || !currentPlan) {
    return { data: null, ...hook };
  }

  const { upgradePlan, viewer } = data;
  const { company } = viewer;
  const { bankAccount, subscriptionPlan } = company;

  const teamMemberCount = company.teamMembers.edges.filter(
    ({ node: { roles, status } }) =>
      roles &&
      roles.some(shouldConsiderRole) &&
      status === TeamMemberStatus.Active,
  ).length;

  return {
    data: {
      currentPlanName: currentPlan.brandName,
      isCapitalDeposit: company.isCapitalDeposit,
      isShineStart: company.isShineStart,
      nextBalance: bankAccount?.nextBalance ?? 0,
      trialPeriodLength: company.subscriptionPlan?.trialPeriodLength ?? 0,
      upgradePlanInfo: {
        cost: subscriptionPlan?.upgradePlanInfo
          ? subscriptionPlan.upgradePlanInfo.addonUpgradeCost +
            subscriptionPlan.upgradePlanInfo.upgradeCost
          : 0,
        extraSeats: {
          seatCost: upgradePlan.fees.extraSeatMonth.fixed,
          withCurrentPlan: Math.max(
            0,
            teamMemberCount - currentPlan.limits.companyUserAccess.value,
          ),
          withNextPlan: Math.max(
            0,
            teamMemberCount - upgradePlan.limits.companyUserAccess.value,
          ),
        },
        nbOfPremiumCards: upgradePlan.limits.physicalPaymentCard.value,
        nextBillingDate: subscriptionPlan?.upgradePlanInfo?.nextBillingDate,
        planId: subscriptionPlan?.upgradePlanInfo?.planId,
        planName: upgradePlan.brandName,
        price: upgradePlan.price,
        priceWithoutVAT: upgradePlan.priceWithoutVAT,
      },
    },
    ...hook,
  };
};
